import React, { useEffect, useState } from "react";
import { wsRequest, onBroadcast } from "./wsClient.js";
import { NavLink } from "./Navigation.jsx";

// Shown at the bottom of every public page (alongside SiteHeader at the
// top). Only shows a link once root has actually published that document
// (HTML content or an uploaded PDF) — an unpublished one is simply
// omitted, not shown as a dead link. Also shows any content page root's
// set to appear in the footer specifically (see SiteHeader.jsx for the
// header-placed equivalent).
export default function Footer() {
  const [docs, setDocs] = useState([]);
  const [pages, setPages] = useState([]);

  const load = () => {
    wsRequest("legalDocs.list")
      .then((list) => setDocs(list.filter((d) => d.htmlUrl || d.pdfUrl)))
      .catch(() => {});
    wsRequest("content.list")
      .then((list) => setPages(list.filter((p) => p.inFooter)))
      .catch(() => {});
  };

  useEffect(load, []);

  // The version this BUNDLE was built from, baked in by vite.config.js —
  // not fetched, deliberately. A stale bundle would report the running
  // backend's version quite happily and look correct.
  //
  // The backend's own version is fetched alongside it, and the two are
  // shown together only when they DIFFER: matching versions are the normal
  // case and do not need saying, while a mismatch means the browser is
  // holding an old frontend, which cost an hour on 28 Aug 2026.
  // GUARDED. If the build ever runs without vite.config.js's define — a
  // different bundler, an older config, someone running esbuild directly —
  // a bare __APP_VERSION__ is a ReferenceError, and this component renders
  // on EVERY public page. A diagnostic aid that can white-screen the site
  // is worse than no diagnostic aid.
  const buildVersion = typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : null;

  const [serverVersion, setServerVersion] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch("/api/v1/version")
      .then((r) => r.json())
      .then((d) => {
        if (!cancelled) setServerVersion(d && d.version ? d.version : null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    return onBroadcast((message) => {
      if (message.type === "legal-docs-changed" || message.type === "content-pages-changed") load();
    });
  }, []);

  // No early return any more: the version line below has to appear even on
  // a site with no legal documents or footer pages published yet, which is
  // every site on its first day.

  return (
    <footer
      style={{
        marginTop: 40,
        padding: "20px 20px",
        borderTop: "1px solid var(--border-color)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 10,
      }}
    >
      <div style={{ display: "flex", justifyContent: "center", gap: 20, flexWrap: "wrap" }}>
      {docs.map((d) =>
        d.htmlUrl ? (
          <NavLink key={d.key} to={d.htmlUrl} style={{ color: "var(--text-secondary)", fontSize: 13, textDecoration: "none" }}>
            {d.label}
          </NavLink>
        ) : (
          // PDF-only document (no HTML page to route to) — new tab for the
          // same reason as the PDF link inside LegalDocPage: this one sits
          // in the footer of every public page, so following it in the same
          // tab would drop the visitor out of the site from wherever they
          // happened to be.
          <a
            key={d.key}
            href={d.pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "var(--text-secondary)", fontSize: 13, textDecoration: "none" }}
          >
            {d.label}
          </a>
        )
      )}
      {pages.map((p) => (
        <NavLink key={p.slug} to={p.url} style={{ color: "var(--text-secondary)", fontSize: 13, textDecoration: "none" }}>
          {p.title}
        </NavLink>
        ))}
      </div>
      {/* THE VERSION, underneath the links.
          Quiet by design — it is for whoever is diagnosing something, not
          for a buyer. The build version is what matters: if the browser is
          holding an old bundle this is the only place that says so, and
          the server version appears beside it ONLY when the two disagree,
          because that disagreement IS the fault. */}
      <div style={{ color: "var(--text-secondary)", fontSize: 11, opacity: 0.6 }}>
        {buildVersion ? "v" + buildVersion : serverVersion ? "v" + serverVersion : ""}
        {buildVersion && serverVersion && serverVersion !== buildVersion ? (
          <span style={{ color: "var(--accent-red)" }}>
            {" "}
            — server is running v{serverVersion}, this page is out of date. Reload.
          </span>
        ) : null}
      </div>
    </footer>
  );
}
